/**
 * The below lint rules are disabled because Box relies on building styles at runtime in Forge apps.
 * This implementation is copied from @atlaskit/primitives/src/components/box.tsx which has the same
 * pattern. The css prop with runtime-computed values is required for the xcss prop to work correctly.
 * See go/DSP-18766 for more context.
 *
 * @atlaskit/design-system/consistent-css-prop-usage is disabled because this component must import
 * shared style maps (backgroundColorStylesMap, surfaceColorStylesMap, paddingStylesMap) for runtime
 * token resolution. Inlining these maps would create unmaintainable code duplication across Box and
 * Pressable components. This is an architectural requirement for Forge UI Kit's token system.
 */
/* eslint-disable @atlaskit/design-system/consistent-css-prop-usage -- Required for runtime xcss resolution with imported token style maps. See explanation above and go/DSP-18766 */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values -- Required for token map imports. See go/DSP-18766 */
/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values -- Required for runtime style computation. See go/DSP-18766 */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css, jsx } from '@emotion/react';
import type { CSSObject } from '@emotion/serialize';
import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { UNSAFE_SurfaceContext } from '@atlaskit/primitives/compiled';

import { xcssValidator, type XCSSProp } from '../../utils/xcssValidator';
import { generateXcss } from '../../utils/xcssValidate';
import {
	backgroundColorStylesMap,
	surfaceColorStylesMap,
	paddingStylesMap,
	isSurfaceColorToken,
	type BackgroundColor,
	type Space,
} from '../tokens.partial';

export type BoxProps = {
	/**
	 * A token alias for background color. See: [Design tokens](https://atlassian.design/components/tokens/all-tokens)
	 * for a list of available colors.
	 *
	 * When the background color is set to a surface token, the current surface CSS variable
	 * will also be set to this value in the `Box` styles.
	 *
	 * @type [Background color tokens](https://atlassian.design/components/tokens/all-tokens#color-background)
	 */
	backgroundColor?: BackgroundColor;

	/**
	 * @type ForgeComponent
	 */
	children?: React.ReactNode;

	/**
	 * A shorthand for `paddingBlock` and `paddingInline` together.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	padding?: Space;

	/**
	 * The logical block start and end padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingBlock?: Space;

	/**
	 * The logical block end padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingBlockEnd?: Space;

	/**
	 * The logical block start padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingBlockStart?: Space;

	/**
	 * The logical inline start and end padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingInline?: Space;

	/**
	 * The logical inline end padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingInlineEnd?: Space;

	/**
	 * The logical inline start padding of an element.
	 *
	 * @type [Space tokens](https://atlassian.design/components/tokens/all-tokens#space)
	 */
	paddingInlineStart?: Space;

	/**
	 * Accessible role.
	 *
	 * @type string
	 */
	role?: string;

	/**
	 * Test ID for the box.
	 *
	 * @type string
	 */
	testId?: string;

	/**
	 * Apply a subset of permitted styles, powered by Atlassian Design System tokens.
	 * For a list of supported style properties on this component, see [here](/platform/forge/ui-kit/components/xcss).
	 *
	 * @type XCSSProp
	 */
	xcss?: XCSSProp;
};

// Base styles for the Box component
const baseStyles = css({
	boxSizing: 'border-box',
	appearance: 'none',
	border: 'none',
});

// Internal Box component implementation (migrated from @atlaskit/primitives)
const InternalBox = ({
	children,
	backgroundColor,
	padding,
	paddingBlock,
	paddingBlockStart,
	paddingBlockEnd,
	paddingInline,
	paddingInlineStart,
	paddingInlineEnd,
	testId,
	xcss: xcssValue,
	role,
}: BoxProps) => {
	const node = (
		<div
			css={[
				baseStyles,
				backgroundColor && backgroundColorStylesMap[backgroundColor],
				isSurfaceColorToken(backgroundColor) && surfaceColorStylesMap[backgroundColor],
				padding && paddingStylesMap.padding[padding],
				paddingBlock && paddingStylesMap.paddingBlock[paddingBlock],
				paddingBlockStart && paddingStylesMap.paddingBlockStart[paddingBlockStart],
				paddingBlockEnd && paddingStylesMap.paddingBlockEnd[paddingBlockEnd],
				paddingInline && paddingStylesMap.paddingInline[paddingInline],
				paddingInlineStart && paddingStylesMap.paddingInlineStart[paddingInlineStart],
				paddingInlineEnd && paddingStylesMap.paddingInlineEnd[paddingInlineEnd],
				xcssValue && generateXcss(xcssValue as CSSObject),
			]}
			data-testid={testId}
			role={role}
		>
			{children}
		</div>
	);

	// Provide surface context so child components (like Heading) can detect
	// the background color and apply inverse text colors automatically
	return backgroundColor ? (
		// eslint-disable-next-line react/jsx-pascal-case -- UNSAFE_ prefix is from @atlaskit/primitives
		<UNSAFE_SurfaceContext.Provider value={backgroundColor}>{node}</UNSAFE_SurfaceContext.Provider>
	) : (
		node
	);
};

/**
 * A box is a generic container that provides managed access to design tokens.
 */
export const Box = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const {
		padding,
		paddingBlock,
		paddingBlockStart,
		paddingBlockEnd,
		paddingInline,
		paddingInlineStart,
		paddingInlineEnd,
		backgroundColor,
		testId,
		xcss,
		role,
	} = props.forgeDoc.props as BoxProps;
	return (
		<InternalBox
			padding={padding}
			paddingBlock={paddingBlock}
			paddingBlockStart={paddingBlockStart}
			paddingBlockEnd={paddingBlockEnd}
			paddingInline={paddingInline}
			paddingInlineStart={paddingInlineStart}
			paddingInlineEnd={paddingInlineEnd}
			backgroundColor={backgroundColor}
			testId={testId}
			xcss={xcss && xcssValidator(xcss)}
			role={role}
		>
			{props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
		</InternalBox>
	);
};
